import React from 'react';
import { UnifiedTransactionForm, UnifiedTransactionPrefill } from './UnifiedTransactionForm';
import { InventarioConsumptionForm } from './InventarioConsumptionForm';
import { VentaInventarioForm } from './VentaInventarioForm';
import type { TipoOperacion } from '../../types/administracion';

interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tipoOperacionInicial: TipoOperacion;
  onSuccess: () => void;
  /** Valores iniciales del gasto (ej. "Cargar monto" de un pago anotado sin monto). */
  prefill?: UnifiedTransactionPrefill;
}

export const TransactionModal: React.FC<TransactionModalProps> = ({
  isOpen,
  onClose,
  tipoOperacionInicial,
  onSuccess,
  prefill,
}) => {
  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  if (!isOpen) return null;

  const getIcon = () => {
    switch (tipoOperacionInicial) {
      case 'COMPRA': return '🛒';
      case 'GASTO': return '💸';
      case 'CONSUMO': return '📉';
      case 'VENTA': return '💰';
      default: return '📝';
    }
  };

  const getTitle = () => {
    switch (tipoOperacionInicial) {
      case 'COMPRA': return 'Registrar Compra / Inventario';
      case 'GASTO': return 'Registrar Gasto';
      case 'CONSUMO': return 'Usar Inventario';
      case 'VENTA': return 'Vender Inventario';
      default: return 'Operación';
    }
  };

  const getDescription = () => {
    switch (tipoOperacionInicial) {
      case 'COMPRA': return 'Agregá productos al inventario o registrá compras';
      case 'GASTO': return 'Registrá gastos directos o compras';
      case 'CONSUMO': return 'Registrá el uso de materiales del inventario';
      case 'VENTA': return 'Registrá una venta de productos del inventario';
      default: return '';
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="rounded-lg border border-hairline bg-paper shadow-overlay max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header sobrio */}
        <div className="border-b border-hairline px-6 py-4 shrink-0">
          <div className="flex justify-between items-start gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-2xl shrink-0" aria-hidden="true">{getIcon()}</span>
              <div className="min-w-0">
                <h2 className="font-display text-lg font-semibold leading-tight text-ink">
                  {getTitle()}
                </h2>
                <p className="mt-0.5 text-sm text-sage">
                  {getDescription()}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Cerrar"
              className="-mr-1 shrink-0 rounded-md p-1.5 text-sage transition-colors hover:bg-paper-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-mid/60"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div className="p-6 overflow-y-auto flex-grow bg-paper-2/40">
          <div className="max-w-3xl mx-auto">
            {(tipoOperacionInicial === 'COMPRA' || tipoOperacionInicial === 'GASTO') && (
              <UnifiedTransactionForm
                onSuccess={handleSuccess}
                onCancel={onClose}
                initialType={tipoOperacionInicial === 'COMPRA' ? 'COMPRA' : 'GASTO'}
                prefill={prefill}
              />
            )}

            {tipoOperacionInicial === 'CONSUMO' && (
              <InventarioConsumptionForm
                onSuccess={handleSuccess}
                onCancel={onClose}
              />
            )}

            {tipoOperacionInicial === 'VENTA' && (
              <VentaInventarioForm
                onSuccess={handleSuccess}
                onCancel={onClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionModal;